import React, { useEffect, useState, useMemo } from "react";
import classNames from "classnames";

import {
	Modal,
	Button
} from "rhombus-react";

import { useDispatch, useSelector } from "react-redux";
import { reset } from "redux-form";

import { fetchSingleVersion } from "reducers/CreativeVersions";
import { changeTab } from "reducers/Social";
import { loadVersionDetails } from "reducers/VersionDetails";

import { isLegacyPreview, PreviewVersion, LegacyPreviewVersion } from "utils/Preview";
import { ADSTUDIO_IFRAME_ASSET_DIMENSIONS, API_URL, SOCIAL_AD_TYPES, VERSION_STATUS } from "utils/constants";
import {
	isSocialCreative,
	buildAdStudioIframeUrl,
	isAdstudioCreative,
	iframeDimsRegex,
	scaleIframe,
	getManifest
} from "utils/common";

import { LABEL_MANAGEMENT } from "widgets/forms/list";
import {
	getSocialValuesFromVariables,
	makeAdstudioSimpleAssetUrl,
	makeAdstudioCarouselAssetUrl,
	makeAdstudioPacAssetUrl
} from "widgets/social2/utils";
import ProgressContainer from "widgets/ProgressContainer";
import VersionPreviewBladeToolbar from "widgets/modals/versions/VersionPreviewBladeToolbar";
import LabelsPanel from "widgets/modals/creatives/preview/LabelsPanel";

import useFetchFallback from "hooks/useFetchFallback";

import PreviewVersionBladeContent from "./PreviewVersionBladeContent";
import { FACEBOOK_TAB } from "../creatives/social/Tabs";

import "./PreviewVersionBlade.css";

const fbMOTypes = [
	SOCIAL_AD_TYPES.FACEBOOK_IMAGE_MO,
	SOCIAL_AD_TYPES.FACEBOOK_VIDEO_MO,
	SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL_MO
];

export default function PreviewVersionBlade(props){
	const dispatch = useDispatch();
	let { onClose, creativeId, libraryId, versionId } = props
		, [isFullScreen, setIsFullScreen] = useState(false)
		, [versionInfo, setVersionInfo] = useState()
		, [randomKey, setRandomKey] = useState(0)
		, [isMuted, setIsMuted] = useState(true)
		, {
			isLoaded,
			versionDetails,
			selectedTab,
			aspectRatio,
			placement,
			creativeDetails,
		} = useSelector((state)=>{
			return {
				isLoaded:state.creativeVersions.isLoaded,
				versionDetails:state.versionDetails,
				selectedTab:state.social.selectedTab.value,
				aspectRatio:state.social.selectedAspectRatio,
				placement:state.social.facebookPlacementType.value,
				creativeDetails:state.creativeDetails,
			};
		})
		, title =  versionInfo?.name || "Loading"
		;

	const selection = useFetchFallback({
		selector: state => state.creativeVersions.items?.find(item => item.id === parseInt(versionId)),
		fetcher: () => dispatch(fetchSingleVersion(libraryId, creativeId, versionId)),
		shouldFetch: isLoaded,
	});

	const { width, height, socialValues = {}, adType, assetType, approval } = creativeDetails || {};

	const isLegacy = isLegacyPreview(creativeDetails);
	const isAdStudio = isAdstudioCreative(creativeDetails);

	let adStudioIframe;
	let adStudioIframeProps;

	const [iframeDimensions, setiframeDimensions] = useState({});
	const isSocial = isSocialCreative(creativeDetails);

	const onReplay = () => {
		setRandomKey(randomKey + 1);
	};

	useEffect(() => {
		if(!isAdStudio || !isSocial){ return; }

		dispatch(getManifest(libraryId, creativeId))
			.then((resp) => {
				if(resp?.success) {
					let parsedResponse = resp.payload.data.match(iframeDimsRegex);
					let [width, height] = parsedResponse;

					let widthVal = width.replace(/"width":/, "").trim();
					widthVal = parseInt(widthVal);

					let heightVal = height.replace(/"height":/, "").trim();
					heightVal = parseInt(heightVal);

					setiframeDimensions({ iframeWidth: widthVal, iframeHeight: heightVal });
				}
			});
	}, [isAdStudio, libraryId, creativeId, isSocial]);

	if(isAdStudio && isSocial) {
		const selectedVersionUnpublished = selection && selection.status === VERSION_STATUS.UNPUBLISHED;

		if(approval !== "APPROVED" || selectedVersionUnpublished) {
			adStudioIframeProps = {
				src: buildAdStudioIframeUrl(libraryId, creativeId),
				frameProps: JSON.stringify({
					adPreview: true,
					ftURLMap: {
						videoBaseURL: "//cdn.flashtalking.com",
						dynamicAds: `${API_URL}/crm/v1/ui/creative-preview/${libraryId}/${creativeId}/${versionId}/variables`,
					},
				}),
			};
		}
	}


	if(adStudioIframeProps) {
		const { width } = ADSTUDIO_IFRAME_ASSET_DIMENSIONS[adType];
		const { iframeWidth, iframeHeight } = iframeDimensions;

		const calculateScale = scaleIframe(width, iframeWidth);

		const style = {
			width: iframeWidth,
			height: iframeHeight,
			transform: `scale(${calculateScale})`,
			transformOrigin: "left top",
		};

		adStudioIframe = (
			<iframe
				className="ad-studio-iframe"
				src={adStudioIframeProps.src}
				scrolling="no"
				name={adStudioIframeProps.frameProps}
				style={style}
			/>
		);
	}

	const iframeUrl = isLegacy ? LegacyPreviewVersion.basic(creativeId, selection?.name) : PreviewVersion.basic(creativeId, selection?.name);
	const previewURL = isLegacy ? LegacyPreviewVersion.private(creativeId, selection?.name) : PreviewVersion.private(creativeId, selection?.name);

	const isTypeFBMO = fbMOTypes.includes(adType);

	useEffect(() => {
		if(isSocial) {
			dispatch(loadVersionDetails(libraryId, creativeId, versionId));
		}
	}, [creativeId, versionId, libraryId, isSocial]);

	let flattenedSocialVals = useMemo(() => {
		if(!isSocial){ return; }

		let versionDetailItems = {};
		versionDetails?.items.forEach((item) => {
			versionDetailItems = {
				...versionDetailItems,
				[item.name]:item,
			};
		});

		return getSocialValuesFromVariables(socialValues, versionDetailItems, libraryId, adType);
	}, [versionDetails, socialValues, libraryId, adType]);


	const isCarouselVideo = useMemo(() => {
		if (adType !== SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL_MO) {
			return false;
		}

		const cards = flattenedSocialVals?.cards || [];
		return cards.some(card => card.type === "video");
	}, [flattenedSocialVals, adType]);

	const selectedVersion = selection && selection.status === VERSION_STATUS.LIVE;

	let adjustedSocialVals = useMemo(() => {
		const isValid = approval === "APPROVED" && selectedVersion?.status === "Live" && assetType === "adstudio";

		if(!isValid) { return flattenedSocialVals; }

		if(!flattenedSocialVals?.placements && !flattenedSocialVals?.cards) {
			if(flattenedSocialVals?.image) {
				return {
					...flattenedSocialVals,
					image: {
						...flattenedSocialVals.image,
						src: makeAdstudioSimpleAssetUrl(creativeDetails, libraryId, versionId, "png", "image"),
					},
				};
			}

			if(flattenedSocialVals?.video) {
				return {
					...flattenedSocialVals,
					video: {
						...flattenedSocialVals.video,
						src: makeAdstudioSimpleAssetUrl(creativeDetails, libraryId, versionId, "mp4", "video"),
					},
				};
			}
		}

		if(flattenedSocialVals?.placements) {
			const updatePlacementAssetSrc = flattenedSocialVals.placements.map((placement) => {
				const { image, video, type } = placement;
				if(image) {
					return {
						...placement,
						image: {
							...image,
							src: makeAdstudioPacAssetUrl(creativeDetails, libraryId, type.id, versionId, "png", "image"),
						},
					};
				}
				if(video) {
					return {
						...placement,
						video: {
							...video,
							src: makeAdstudioPacAssetUrl(creativeDetails, libraryId, type.id, versionId, "mp4", "video"),
						},
					};
				}
			});

			let updatedSocialVals = {
				...flattenedSocialVals,
				placements: updatePlacementAssetSrc,
			};

			//default placements will always have a pacType id of 1
			if(flattenedSocialVals?.video) {
				return {
					...updatedSocialVals,
					video: {
						...flattenedSocialVals.video,
						src: makeAdstudioPacAssetUrl(creativeDetails, libraryId, 1, versionId, "mp4", "video"),
					},
				};
			}

			if(flattenedSocialVals?.image) {
				return {
					...updatedSocialVals,
					image: {
						...flattenedSocialVals.image,
						src: makeAdstudioPacAssetUrl(creativeDetails, libraryId, 1, versionId, "png", "image"),
					},
				};
			}

			return {
				...updatedSocialVals,
				placements: updatePlacementAssetSrc,
			};
		}

		if(flattenedSocialVals?.cards) {
			const updateCardAssetSrc = flattenedSocialVals.cards.map((card) => {
				const { image, video } = card;
				if(image) {
					return {
						...card,
						image: {
							...image,
							src: makeAdstudioCarouselAssetUrl(creativeDetails, libraryId, card, versionId, "png", "image"),
						},
					};
				}
				if(video) {
					return {
						...card,
						video: {
							...video,
							src: makeAdstudioCarouselAssetUrl(creativeDetails, libraryId, card, versionId, "mp4", "video"),
						},
					};
				}
			});

			return {
				...flattenedSocialVals,
				cards: updateCardAssetSrc,
			};
		}
	}, [
		flattenedSocialVals,
		assetType,
		approval,
		selectedVersion?.status,
		creativeDetails,
		libraryId,
		creativeId
	]);

	let previewProps = useMemo(() => {
		return {
			socialValues: { ...adjustedSocialVals, adStudioIframe },
			baseDomain: API_URL,
			adType,
			isLoaded,
			partner: selectedTab,
			aspectRatio,
			placement,
			// eslint-disable-next-line
			fbPreviewVersion: isTypeFBMO ? 2 : 1,
			muted: isMuted,
		};
	}, [
		adjustedSocialVals,
		adType,
		isLoaded,
		selectedTab,
		aspectRatio,
		placement,
		isTypeFBMO,
		adStudioIframe,
		isMuted,
	]);

	const versionBladeContentProps = {
		isSocial,
		width,
		height,
		iframeUrl: isSocial ? null : iframeUrl,
		isTypeFBMO,
		previewProps,
	};

	useEffect(() => {
		if(selection){
			setVersionInfo(selection);
		}
	}, [selection]);

	function closeModal(){
		onClose();
		dispatch(reset(LABEL_MANAGEMENT)); /*
			We don't destroy the label management form on unmount, so we have to reset it manually
			when the blade closes.
		*/
	}

	function onToggleFullScreen(){
		setIsFullScreen(!isFullScreen);
	}

	function Footer(){
		return [
			<Button key="close" theme="default" onClick={closeModal} className="close-btn">Close</Button>,
		];
	}

	useEffect(() => {
		if(isTypeFBMO) {
			//set initial placement tab to facebook if adtype FBMO
			dispatch(changeTab(FACEBOOK_TAB));
		}
	}, [adType]);

	let versionBladeContentKey = versionBladeContentProps.previewProps.socialValues?.video || versionBladeContentProps.previewProps.socialValues?.image;

	function Content(){
		const isContentLoading = !isLoaded || (!versionDetails.isLoaded && isSocial)
			, contentClasses = {
				"main-content":true,
				"preview-version":true,
				"is-fb":isTypeFBMO,
				"is-PRVW":!isLegacy,
			}
			;

		return (
			<div className="main-content-container">
				<ProgressContainer
					className="preview-container"
					isLoading={isContentLoading}
				>
					<div className={classNames(contentClasses)}>
						<PreviewVersionBladeContent
							key={versionBladeContentKey?.id + randomKey}
							keyId={randomKey}
							{...versionBladeContentProps}
							isLegacy={isLegacy}
						/>
					</div>
				</ProgressContainer>
				<LabelsPanel
					creativeId={creativeId}
					versionId={versionId}
					versionDetails={versionInfo}
				/>
				<VersionPreviewBladeToolbar
					previewURL={previewURL}
					setIsMuted={setIsMuted}
					isCarouselVideo={isCarouselVideo}
					onReplay={onReplay}
					muted={isMuted}
					adType={adType}
				/>
			</div>
		);
	}

	let modalProps = {
		title,
		type: "blade",
		className: "preview-version-blade expandable",
		setAppTitle:false,

		xClose: true,
		allowFullScreen: true,
		isFullScreen,
		onToggleFullScreen,

		renderFooter: Footer,
		renderContent: Content,

		onClose: closeModal,
	};

	return <Modal {...modalProps} />;
}
